import type {
  LockerverseCustomField,
  LockerverseWidgetFields,
} from "@lockerverse/sdk/checkout";
import { useId } from "react";
import { CustomFields, type CustomFieldValues } from "./custom-fields.tsx";
import { Checkbox } from "./form-field.tsx";
import { formatMoney } from "./format-money.ts";
import { LockerverseSelect } from "./select-control.tsx";

export const MERCH_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"] as const;

export type CheckoutFieldValues = {
  customTip: string;
  customFields: CustomFieldValues;
  includeServiceFee: boolean;
  memberId: string;
  merchSize: (typeof MERCH_SIZES)[number];
  promoCode: string;
  shippingMatchesBilling: boolean;
  tipPercent: "5" | "10" | "12" | "15" | "custom";
};

const TIP_OPTIONS = ["15", "12", "10", "5"] as const;
const TIP_VALUES = [...TIP_OPTIONS, "custom"] as const;

export function TipField({
  enabled,
  error,
  errorId,
  onChange,
  values,
}: {
  enabled: boolean;
  error: string | undefined;
  errorId: string;
  onChange: (values: CheckoutFieldValues) => void;
  values: CheckoutFieldValues;
}) {
  if (!enabled) {
    return null;
  }
  const update = (next: Partial<CheckoutFieldValues>) =>
    onChange({ ...values, ...next });
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    currentValue: CheckoutFieldValues["tipPercent"]
  ) => {
    const currentIndex = TIP_VALUES.indexOf(currentValue);
    let nextIndex: number | undefined;
    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        nextIndex = (currentIndex + 1) % TIP_VALUES.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = (currentIndex - 1 + TIP_VALUES.length) % TIP_VALUES.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = TIP_VALUES.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const nextValue = TIP_VALUES[nextIndex];
    if (!nextValue) {
      return;
    }
    const radios = event.currentTarget
      .closest('[role="radiogroup"]')
      ?.querySelectorAll<HTMLElement>('input[type="radio"]');
    radios?.[nextIndex]?.focus();
    update({ tipPercent: nextValue });
  };
  return (
    <div className="lockerverse-payment__tip-box">
      <p>Add a tip to support the Lockerverse platform</p>
      <div aria-label="Tip amount" role="radiogroup">
        {TIP_OPTIONS.map((tip) => (
          <label className="lockerverse-payment__tip-choice" key={tip}>
            <input
              checked={values.tipPercent === tip}
              name="tipPercent"
              onChange={() => update({ tipPercent: tip })}
              onKeyDown={(event) => handleKeyDown(event, tip)}
              tabIndex={values.tipPercent === tip ? 0 : -1}
              type="radio"
              value={tip}
            />
            <span>{tip}%</span>
          </label>
        ))}
        <label className="lockerverse-payment__tip-choice">
          <input
            checked={values.tipPercent === "custom"}
            name="tipPercent"
            onChange={() => update({ tipPercent: "custom" })}
            onKeyDown={(event) => handleKeyDown(event, "custom")}
            tabIndex={values.tipPercent === "custom" ? 0 : -1}
            type="radio"
            value="custom"
          />
          <span>Other</span>
        </label>
      </div>
      {values.tipPercent === "custom" ? (
        <label>
          <span>Custom tip</span>
          <span className="lockerverse-payment__money-input">
            <span aria-hidden="true">$</span>
            <input
              aria-describedby={error ? errorId : undefined}
              aria-invalid={error ? "true" : undefined}
              inputMode="decimal"
              name="tip"
              onChange={(event) =>
                update({ customTip: event.currentTarget.value })
              }
              placeholder="0.00"
              value={values.customTip}
            />
          </span>
          {error ? (
            <span className="lockerverse-payment__field-message" id={errorId}>
              {error}
            </span>
          ) : null}
        </label>
      ) : (
        <input name="tip" type="hidden" value={values.tipPercent} />
      )}
    </div>
  );
}

export function CheckoutToggle({
  checked,
  children,
  name,
  onChange,
}: {
  checked: boolean;
  children: React.ReactNode;
  name: string;
  onChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <label className="lockerverse-payment__check-row" htmlFor={id}>
      <Checkbox
        checked={checked}
        id={id}
        name={name}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      <span>{children}</span>
    </label>
  );
}

export function AdditionalCheckoutFields({
  customFields,
  errors,
  fields,
  idPrefix,
  isPromoApplying,
  memberIdLabel,
  onChange,
  onApplyPromo,
  promoError,
  values,
}: {
  customFields: LockerverseCustomField[];
  errors: Record<string, string>;
  fields: LockerverseWidgetFields;
  idPrefix: string;
  isPromoApplying: boolean;
  memberIdLabel: string | null | undefined;
  onChange: (values: CheckoutFieldValues) => void;
  onApplyPromo: () => void;
  promoError: string | undefined;
  values: CheckoutFieldValues;
}) {
  const update = (next: Partial<CheckoutFieldValues>) =>
    onChange({ ...values, ...next });

  return (
    <div className="lockerverse-payment__additional-fields">
      {fields.memberId ? (
        <label>
          <span>{memberIdLabel?.trim() || "Member ID"}</span>
          <input
            name="memberId"
            onChange={(event) =>
              update({ memberId: event.currentTarget.value })
            }
            placeholder="Optional"
            value={values.memberId}
          />
        </label>
      ) : null}
      {fields.merchSize ? (
        <div className="lockerverse-payment__field">
          <span id={`${idPrefix}-merch-size-label`}>Size</span>
          <LockerverseSelect
            ariaLabelledBy={`${idPrefix}-merch-size-label`}
            name="merchSize"
            onValueChange={(value) => {
              const merchSize = MERCH_SIZES.find((size) => size === value);
              if (merchSize) {
                update({ merchSize });
              }
            }}
            options={MERCH_SIZES.map((size) => ({
              label: size,
              value: size,
            }))}
            value={values.merchSize}
          />
        </div>
      ) : null}
      {fields.promoCode ? (
        <div className="lockerverse-payment__field">
          <span id={`${idPrefix}-promo-code-label`}>Promo code</span>
          <div className="lockerverse-payment__promo-control">
            <input
              aria-describedby={
                promoError ? `${idPrefix}-promo-code-error` : undefined
              }
              aria-invalid={promoError ? "true" : undefined}
              aria-labelledby={`${idPrefix}-promo-code-label`}
              name="promoCode"
              onChange={(event) =>
                update({ promoCode: event.currentTarget.value })
              }
              placeholder="Optional"
              value={values.promoCode}
            />
            <button
              disabled={!values.promoCode.trim() || isPromoApplying}
              onClick={onApplyPromo}
              type="button"
            >
              {isPromoApplying ? "Applying…" : "Apply"}
            </button>
          </div>
          {promoError ? (
            <p
              className="lockerverse-payment__field-message"
              id={`${idPrefix}-promo-code-error`}
              role="alert"
            >
              {promoError}
            </p>
          ) : null}
        </div>
      ) : null}
      <CustomFields
        choiceClassName="lockerverse-payment__check-row"
        errorClassName="lockerverse-payment__field-message"
        errors={errors}
        fields={customFields}
        idPrefix={idPrefix}
        onChange={(nextCustomFields) =>
          update({ customFields: nextCustomFields })
        }
        values={values.customFields}
      />
    </div>
  );
}

export function ServiceFeeField({
  amountCents,
  enabled,
  onChange,
  values,
}: {
  amountCents: number;
  enabled: boolean;
  onChange: (values: CheckoutFieldValues) => void;
  values: CheckoutFieldValues;
}) {
  if (!enabled) {
    return null;
  }
  return (
    <CheckoutToggle
      checked={values.includeServiceFee}
      name="includeServiceFee"
      onChange={(includeServiceFee) =>
        onChange({ ...values, includeServiceFee })
      }
    >
      Add {formatMoney(amountCents)} to help cover processing fees
    </CheckoutToggle>
  );
}
